import React, {
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from 'react';
import { ThemeContext } from '@context/ThemeContext';
import GlobalChart from '@components/redesign/GlobalChart';
import adoptionRateByPersona from '@v2/charts/tpo/adoptionRateByPersona';
import KpiContainer from '@components/redesign/KpiContainer';
import { useDynamicGraphQLQuery } from '@hooks/useDynamicGraphQLQuery';
import FilterWrapper from '@v2/components/GDS/Filter';

interface AdoptionRateByPersonaProps {
  title?: string;
  selectedMarkets?: Array<{ id: string; displayText: string }>;
}

const AdoptionRateByPersona: React.FC<AdoptionRateByPersonaProps> = ({
  title = 'Adoption Rate By Persona',
  selectedMarkets = [],
}) => {
  const { mode } = useContext(ThemeContext);

  // State for managing selected persona type
  const [selectedPersonaType, setSelectedPersonaType] = useState<{
    id: string;
    label: string;
  } | null>(null);

  // Extract market IDs for the GraphQL query - memoized to prevent re-computation
  const marketIds = useMemo(() => {
    return selectedMarkets.length > 0
      ? selectedMarkets.map((market) => parseInt(market.id))
      : [];
  }, [selectedMarkets]);

  const { data: personaTypeFilter, isLoading: personaTypeFilterLoading } =
    useDynamicGraphQLQuery('getPersonaDetailByBU', { marketIds });

  const {
    data: adoptionRateByMktData,
    isLoading: adoptionRateByMktLoading,
    setShouldFetch,
  } = useDynamicGraphQLQuery(
    'getAdoptionRateByMarket',
    {
      marketIdList: marketIds.length > 0 ? marketIds : [],
      persona: selectedPersonaType?.id,
    },
    false
  );

  // Transform persona type data to FilterWrapper format - memoized
  const personaTypeOptions = useMemo(() => {
    return (
      personaTypeFilter?.getPersonaDetailByBU?.data?.map(
        (personaType: any) => ({
          id: personaType.personaName,
          label: personaType.personaDisplayName,
        })
      ) || []
    );
  }, [personaTypeFilter]);

  // Set default persona type when options change - prefer 'CORE_USER' if available
  useEffect(() => {
    if (personaTypeOptions.length > 0) {
      // Try to find 'CORE_USER' first
      const coreUserOption = personaTypeOptions.find(
        (option) => option.id === 'CORE_USER'
      );

      // Use 'CORE_USER' if found, otherwise use first option
      const defaultOption = coreUserOption || personaTypeOptions[0];
      setSelectedPersonaType(defaultOption);
    }
  }, [personaTypeOptions]);

  // Trigger data fetch when persona filter loading completes - simplified
  useEffect(() => {
    if (!personaTypeFilterLoading) {
      setShouldFetch(true);
    }
  }, [personaTypeFilterLoading, setShouldFetch]);

  // Handle persona type filter change - memoized callback
  const handlePersonaTypeChange = useCallback(
    (newPersonaType: { id: string; label: string }) => {
      if (newPersonaType) {
        setSelectedPersonaType(newPersonaType);
      }
    },
    []
  );

  // Create the persona type filter component
  const personaTypeFilterComponent = (
    <FilterWrapper
      options={personaTypeOptions}
      value={selectedPersonaType}
      onChange={handlePersonaTypeChange}
      disabled={personaTypeOptions.length === 0 || personaTypeFilterLoading}
      label="Persona Type"
      data-testid="adoption-rate-persona-type-filter"
    />
  );

  // Memoize chart options computation
  const chartOption = useMemo(() => {
    if (adoptionRateByMktLoading) {
      return {};
    }
    try {
      return adoptionRateByPersona.handler(
        adoptionRateByMktData?.getAdoptionRateByMarket?.data || [],
        mode
      );
    } catch (error) {
      return {};
    }
  }, [adoptionRateByMktLoading, adoptionRateByMktData, mode]);

  // Memoize no data check
  const noDataFound = useMemo(() => {
    return !adoptionRateByMktData?.getAdoptionRateByMarket?.data?.length;
  }, [adoptionRateByMktData]);

  return (
    <KpiContainer title={title} headerControls={[personaTypeFilterComponent]}>
      <GlobalChart
        noDataFound={noDataFound}
        options={chartOption}
        isLoading={adoptionRateByMktLoading}
        bottomMargin={20}
        customLeftMargin={30}
      />
    </KpiContainer>
  );
};

export default AdoptionRateByPersona;
